/* eslint-disable  @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { useEffect, useRef, useState } from 'react'; // we need this to make JSX
import { useForm } from 'react-hook-form';
import { Grid, Typography, Divider, Link, Drawer } from '@mui/material';
import { useHistory } from 'react-router';
import { useMediaQuery } from 'react-responsive'
import ReCAPTCHA from "react-google-recaptcha";
import moment from 'moment';
import { AUTH_STRINGS } from '../../../constants/strings';
import { Logger } from '../../../libs/utils/logger';
import HttpClient from '../../../libs/utils/httpClient';
import { FxTextEdit } from '../../Input/FxText/FxTextEdit';
import { FxButton } from '../../Action/FxButton';
import { clean, convertToPhoneRequestFormat, getBase64FromPDF, maskEmail, encrypt, passwordPatternRule, namePatternRule, handlePasswordMatchChange, setPhoneValidationOptions, PASSWORD_MAX_LENGTH } from '../../Utils/CommonBaseClass';
import { ReactComponent as BackIcon } from '../../../assets/svg/signup-back.svg';
import { RegisterComponent } from '../../../libs/saga/dataSaga';
import { FxLink } from '../../Action/FxLink';
import DEFAULT_CONFIG from '../../../configs/default.json'
import { processAPIResponse, pushGTM, setFullStoryEvents, setFullStoryProperties } from '../../../libs/utils/utils';
import { FxOtpInput } from '../../Input/FxOtpInput/FxOtpInput';
import { FxResendOtp } from '../../Data/FxResendOtp';
import { FxButtonGroup } from '../../Action/FxButtonGroup';
import clsx from "clsx";
import FxLocker, { IFxLocker } from '../../Action/FxLocker';
import FxSnackBar from '../../Utils/fx-snack-bar';
import { CustomerType } from '../../../types/common.interfaces';
import { setLocalStorage } from '../../../libs/utils/cacheHandler';
import AuthService from '../../../libs/services/authService';
import { renderApp } from '../../..';
import { ReactComponent as InfoTooltip } from '../../../assets/svg/signup/info-tooltip.svg';
import { getKey, isUserLoggedIn, setKey } from '../../../libs/utils/storageManager';
import { ReactComponent as ShieldSignupLeftpanel } from '../../../assets/svg/signup/shield-signup-leftpanel.svg';
import { ReactComponent as BrandIcon } from '../../../assets/svg/signup/brand-logo.svg';
import { ReactComponent as NeedHelpButton } from '../../../assets/svg/signup/NeedHelpButton.svg';
import { ContactUsDrawer } from './ContactUsDrawer';
import FxPhoneEdit from '../../Input/FxPhone/FxPhoneEdit';

Logger.debug("SignUpFlow.tsx", "Signup flow initializing")

interface InputErrType {
    type: string;
    message: string;
}

/**
 * Component: SignupFlow
 * Usage: User signup flow page
 */
export const SignUpFlow: React.FC<any> = React.memo(
    (props) => {
        const lockerRef = useRef<IFxLocker>(null);
        ({ props } = RegisterComponent(props));
        const [isModalOpen, setIsModalOpen] = useState(false);
        const { register,getValues, trigger, formState: { errors, isSubmitting,isValid }, handleSubmit, setValue, control, clearErrors, setError, resetField, watch } = useForm({mode: 'onChange',reValidateMode:'onChange'});
        // default data to be passed to the state
        const defDetails: any = {
            verifyScreen: false,
            channel: 'EMAIL',
            step: 1,
            link: false,
            isTokenVerified: true,
            customerType: props.details.type ? props.details.type : CustomerType.INDIVIDUAL,
            captchaToken: false
        }
        const history = useHistory();
        const isSmallScreen = useMediaQuery({ query: '(max-width: 999px)' })
        const isMobileScreen = useMediaQuery({ query: '(max-width: 600px)' })
        // details state holds the data for this page
        const [details, setDetails] = useState<any>(defDetails);
        // formReq state holds the request for the sign up form
        const [formReq, setFormReq] = useState<any>();
        // token state holds the token in the response of the validating api
        const [token, setToken] = useState<any>();
        const invitationType = getKey('invitationType')
        const isJTCoOwner =props?.details?.isPrimaryOwner==="false" || false;
        const individualCustomer = details?.customerType === CustomerType.INDIVIDUAL || details?.customerType === CustomerType.JOINT_TENANCY
        // setting default values from the link


        const hasPrefilledEmail = Boolean(props.details?.email)
        const isEditableEmail = invitationType === 'SMS' || !hasPrefilledEmail

        // ▶️ MOVE THIS into useEffect:
        useEffect(() => {
            if (!props.details) return

            // sync invite codes
            setValue('inviteCode', props.details.inviteCode)
            setValue('inviteCodeB', props.details.inviteCode)

            // sync email if present
            if (props.details.email) {
            setValue('email', props.details.email)
            setValue('emailB', props.details.email)
            }

            // fullstory side-effect
            setFullStoryProperties('user', {
            email: props.details.email,
            signUpDate: new Date().toISOString(),
            })
        }, [
            props.details,  // rerun whenever details object changes
            setValue,       // stable by react-hook-form
        ])

        // radio options for account type
        const typeData = [
            { id: 'individual', value: 'INDIVIDUAL', label: 'INDIVIDUAL', selected: details?.customerType === 'INDIVIDUAL' ? true : false },
            { id: 'business', value: 'BUSINESS', label: 'BUSINESS', selected: details?.customerType === CustomerType.BUSINESS ? true : false }
        ];


        // Method handles the api call for verifying invite code, phone number and email details
        async function verifyDetails(payloadData: any, inviteCode: any) {
            try {
                const httpClientNoBearer = HttpClient.newClientCustomHeadersNoBearer({})
                HttpClient.unsetToken();
                const data: any = await httpClientNoBearer.post('/invite/' + inviteCode + '/validate', payloadData).then(response => {
                    setToken(response?.data?.token)
                    return response;
                })
                    .catch((error) => {
                        return { ...error };
                    })
                return data;
            } catch (err) {
                return err;
            }
        }

        // Method handles the api call for creating customer
        async function createCustomer(payloadData: any) {
            try {
                const httpClientNoBearer = HttpClient.newClientCustomHeadersNoBearer({})

                HttpClient.unsetToken();
                const data: any = await httpClientNoBearer.post('/customer/signUp', payloadData).then(response => {
                    return response;
                })
                    .catch((error) => {
                        return { ...error };
                    })
                return data;
            } catch (err) {
                return err;
            }
        }
        /**
         * Password Error render function
         */
        const renderPasswordError = (err: InputErrType): string => {
            return err?.message;
        };
        // Email error render function
        const renderEmailError = (err: InputErrType): string => {
            if (err.type === 'required') {
                return AUTH_STRINGS.ERRORS.EMAIL_PLEASE_ENTER;
            }
            return err.message;
        };
        // Mobile error render function
        const renderMobileError = (err: InputErrType): string => {
            if (err.type === 'required') {
                return AUTH_STRINGS.ERRORS.MOBILE_NUMBER_PLEASE_ENTER;
            }
            return err.message;
        };

        // Error render function
        const renderError = (err: InputErrType): string => {
            if (err.type === 'required') {
                return 'This is required';
            } else if (err.type === 'maxLength') {
                return 'Maximum length is 40 characters';
            }
            return err.message;
        };
        // Email error render function
        const setEmailValidationOptions = {
            required: true,
            maxLength: 250,
            pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,40}$/i,
                message: AUTH_STRINGS.ERRORS.EMAIL_INVALID_REGEXP,
            },
        };
        // Method handles the resend code functionality
        const handleResendCode = async () => {
            resetField("enterCode");
            lockerRef.current?.lock();
            const verifyDetailsReq: any = {
                email: individualCustomer ? formReq['email'] : formReq['emailB']
            }
            if(formReq['username']){
                verifyDetailsReq['portalAccess'] = {
                    username: formReq['username']
                }
            }
            let status: any = await verifyDetails(verifyDetailsReq, individualCustomer ? formReq['inviteCode'] : formReq['inviteCodeB']);
            status = processAPIResponse(status)
            if (status.status) {
                // otp send success
                FxSnackBar.show({
                    severity: 'success',
                    text: 'Otp sent successfully',
                });
            }
            else {
                //otp send failed
                FxSnackBar.show({
                    text: status.message,
                });
            }
        }


        // Code Error render function
        const renderCodeError = (err: InputErrType): string => {
            if (err.type === "required") {
                return "Please enter the OTP";
            }
            return err.message;
        };

        // Method handles to redirect to go to back
        const handleBack = () => {
            setDetails({
                ...details,
                ['step']: 1,
                ['verifyScreen']: false,
                ['captchaToken']: false
            });
        }
        // Method handles the onchange of the account type
        const handleTypeChange = (item: any) => {
            try {
                if (item.value) {
                    setDetails({
                        ...details,
                        ['customerType']: item.value
                    })
                    setValue('type', item.value)
                }
            }
            catch (e) { 
                Logger.error("SignUpFlow.tsx", "error", e); 
            }
        }
        // Method handles the onchange event of the captcha
        const handleCaptchaChange = (value: any) => {
            try {
                setDetails({
                    ...details,
                    ['captchaToken']: value ? true : false
                })
                if (value) {
                    clearErrors('captcha');
                }
            }
            catch (e) { 
                Logger.error("SignUpFlow.tsx", "error", e); 
            }
        }

        // Method to handle on submit event for sign up form
        async function onSubmit(data?: any) {
            let req: any;
            if (!details?.captchaToken) {
                setError("captcha", {
                    type: "manual",
                    message: "Please verify that you are not a robot",
                });
                return;
            }
            if (details?.customerType === CustomerType.BUSINESS) {
                let beneficialOwner:any = {
                    firstName: data['firstNameB'],
                    lastName: data['lastNameB'],
                    email: data['emailB'],
                    actAsAuthorizedSignatory: "true",
                }
                if(invitationType === "SMS"){
                    beneficialOwner['mobilePhone'] = convertToPhoneRequestFormat(data['phoneNumber'])
                }
                req = {
                    type: CustomerType.BUSINESS,
                    business: {
                        beneficialOwner:[beneficialOwner],
                        legalName: data['legalName'],
                    },
                    inviteCode: data['inviteCodeB'],
                    sessionMetadata: props.sessionMetadata
                }
                data['username'] === "" && delete req?.business?.beneficialOwner?.[0]?.portalAccess
            }
            else if(details?.customerType === CustomerType.JOINT_TENANCY)
            {
                let owner:any = {
                    isPrimaryOwner:!isJTCoOwner,
                    email: data['email'],
                    firstName: data['firstName'],
                    lastName: data['lastName'],
                    portalAccess: {
                        grantAccess: true
                    }
                }
                if(invitationType === "SMS"){
                    owner['mobilePhone'] = convertToPhoneRequestFormat(data['phoneNumber'])
                }
                req = {
                    type: CustomerType.JOINT_TENANCY,
                    owners: [owner],
                    inviteCode: data['inviteCode'],
                    sessionMetadata: props.sessionMetadata
                }
                data['username'] === "" && delete req?.owners?.portalAccess

            }else
            {
                let individual:any = {
                    email: data['email'],
                    firstName: data['firstName'],
                    lastName: data['lastName'],
                }
                if(invitationType === "SMS"){
                    individual['mobilePhone'] = convertToPhoneRequestFormat(data['phoneNumber'])
                }
                req = {
                    type: details?.customerType,
                    individual: individual,
                    inviteCode: data['inviteCode'],
                    sessionMetadata: props.sessionMetadata
                }
                data['username'] === "" && delete req?.individual?.portalAccess

            }
            const pass_word=details?.customerType==="BUSINESS"?data['newPasswordB']:data['newPassword'];
            req['credentials'] = {
                "type": "PASSWORD",
                "value": encrypt(pass_word)
            };
            req['linkedDocument'] =[{
                purpose: "END_USER_LICENSE_AGREEMENT",
                document: {
                    "type": "END_USER_LICENSE_AGREEMENT",
                    "name": "End_user_license_agreement.pdf",
                    "base64encodedContent": await getBase64FromPDF('../../../../assets/pdf/account_opening_terms.pdf')
                }
            }];
            req = clean(req);
            setDetails({
                ...details,
            })
            let status = await createCustomer(req);
            setDetails({
                ...details,
            })
            status = processAPIResponse(status)
            if (status.status) {
                //api success
                // configuring gtm for user sign up success page
                let gtmData: any;
                if (details?.customerType === CustomerType.BUSINESS) {
                    gtmData = {
                        event: "userSignupSuccessTrigger",
                        "userInvitationCode": data['inviteCodeB'],
                        "userType": details?.customerType,
                        "firstName": data['firstNameB'],
                        "lastName": data['lastNameB'],
                        "legalName": data['legalName']
                    }
                }
                else {
                    gtmData = {
                        event: "userSignupSuccessTrigger",
                        "userInvitationCode": data['inviteCode'],
                        "userType": details?.customerType,
                        "firstName": data['firstName'],
                        "lastName": data['lastName']
                    }
                }
                // configuring fullstory for user sign up success page
                setFullStoryEvents('userInvitationOtpVerifyTrigger',{
                    "userInvitationCode": props.details.inviteCode,
                    "userType": details?.customerType
                });
                pushGTM(gtmData)
                setLocalStorage('keycloack-accesstoken',status?.responseData?.accessToken);
                setLocalStorage('keycloack-refreshToken',status?.responseData?.refreshToken);
                setLocalStorage('signupCustomerType',details?.customerType);
                setLocalStorage('signUpFlow',true);
                AuthService.initKeycloak(renderApp).then(() => {
                    if (details?.customerType === 'BUSINESS') {
                        if (status?.responseData?.userType === "PARTNER") {
                            setKey('loginType', "PARTNER");
                            window.location.href = '/signup/partner/welcome';
                        } else {
                            window.location.href = '/signup/business/kyc';
                        }
                    } else if (details?.customerType === 'INDIVIDUAL') {
                        window.location.href = '/signup/individual/kyc';
                    } else if (details?.customerType === 'JOINT_TENANCY') {
                        const url = isJTCoOwner ? '/signup/joint_tenancy_co_owner/kyc' : '/signup/joint_tenancy/kyc';
                        window.location.href = url;
                    }
                })
            }
            else {
                //api  failed
                FxSnackBar.show({
                    text: status.message,
                });
                if(status.errorCode !== 'EC-BL-0102'){
                    setDetails({
                        ...details,
                        ['step']: 1,
                        ['verifyScreen']: false,
                    });               
                }
            }
        };
        // Method to handle on submit event for verify mobile number form
        async function onSubmitVerify(data?: any) {
            let req: any;
            if (details?.customerType === CustomerType.BUSINESS) {
                req = {
                    type: CustomerType.BUSINESS,
                    business: {
                        beneficialOwner:[
                            {
                                firstName: formReq['firstNameB'],
                                lastName: formReq['lastNameB'],
                                email: formReq['emailB'],
                                actAsAuthorizedSignatory: "true",
                            }
                        ],
                        legalName: formReq['legalName'],
                    },
                    inviteCode: formReq['inviteCodeB'],
                    otpDetails: {
                        otp: data['enterCode'],
                        token: token
                    },
                    sessionMetadata: props.sessionMetadata
                }
                formReq['username'] === "" && delete req?.business?.beneficialOwner?.[0]?.portalAccess
            }
            else if(details?.customerType === CustomerType.JOINT_TENANCY)
            {
                req = {
                    type: CustomerType.JOINT_TENANCY,
                    owners: [{
                        isPrimaryOwner:isJTCoOwner ? false : true,
                        email: formReq['email'],
                        mobilePhone: convertToPhoneRequestFormat(formReq['mobileNumber']),
                        firstName: formReq['firstName'],
                        lastName: formReq['lastName'],
                        countryCode: formReq['mobileNumber-select']?.replace('+', ''),
                        portalAccess: {
                            grantAccess: true
                        }
                    }],
                    inviteCode: formReq['inviteCode'],
                    otpDetails: {
                        otp: data['enterCode'],
                        token: token
                    },
                    sessionMetadata: props.sessionMetadata
                }
                formReq['username'] === "" && delete req?.owners?.portalAccess

            }else
            {
                req = {
                    type: details?.customerType,
                    individual: {
                        email: formReq['email'],
                        firstName: formReq['firstName'],
                        lastName: formReq['lastName'],
                    },
                    inviteCode: formReq['inviteCode'],
                    otpDetails: {
                        otp: data['enterCode'],
                        token: token
                    },
                    sessionMetadata: props.sessionMetadata
                }
                formReq['username'] === "" && delete req?.individual?.portalAccess

            }
            req = clean(req);
            setDetails({
                ...details,
            })
            let status = await createCustomer(req);
            setDetails({
                ...details,
            })
            status = processAPIResponse(status)
            if (status.status) {
                //api success
                // configuring gtm for user sign up success page
                let gtmData: any;
                let fsData:any;
                if (details?.customerType === CustomerType.BUSINESS) {
                    gtmData = {
                        event: "userSignupSuccessTrigger",
                        "userInvitationCode": formReq['inviteCodeB'],
                        "userType": details?.customerType,
                        "firstName": formReq['firstNameB'],
                        "lastName": formReq['lastNameB'],
                        "legalName": formReq['legalName']
                    }
                    fsData={
                        "userInvitationCode": formReq['inviteCodeB'],
                        "userType": details?.customerType,
                        "firstName": formReq['firstNameB'],
                        "lastName": formReq['lastNameB'],
                        "legalName": formReq['legalName']
                    }
                }
                else {
                    gtmData = {
                        event: "userSignupSuccessTrigger",
                        "userInvitationCode": formReq['inviteCode'],
                        "userType": details?.customerType,
                        "firstName": formReq['firstName'],
                        "lastName": formReq['lastName']
                    }
                    fsData={
                        "userInvitationCode": formReq['inviteCode'],
                        "userType": details?.customerType,
                        "firstName": formReq['firstName'],
                        "lastName": formReq['lastName']  
                    }
                }
                pushGTM(gtmData)
                setFullStoryEvents('userSignupSuccessTrigger',fsData);
                setTimeout(() => {
                    history.push('/signup/success');
                }, 1000);
            }
            else {
                //api  failed
                FxSnackBar.show({
                    text: status.message,
                });
                if(status.errorCode !== 'EC-BL-0102'){
                    setDetails({
                        ...details,
                        ['step']: 1,
                        ['verifyScreen']: false,
                        ['captchaToken']: false
                    });               
                }
            }
        };

        const handleExportModalOpen = () => {
            setIsModalOpen(true);
          };
    /**
     * Closes the drawer component.
     *
     * This function sets the `openDrawer` state to false,
     * effectively closing the drawer in the UI.
    */
    const handleCloseDrawer = () => {
        setIsModalOpen(false);
    };
        return (
            <>
            <Grid item xs={12} container direction="row" className="fx-signup" >
                <Grid item xs={12} container direction="row" alignContent="flex-start" sm={isSmallScreen ? undefined : 12}  >
                    <div className="login-page">
                        <Grid xs={12} sm={10} container justifyContent="center">
                            <Grid xs={12} sm={10} md={5} className={"login-page-content-signup"}>
                               
                                <Grid item xs={12}>
                                    {/*             Title for sign up first screen                     */}
                                    {/*             Title for signup otp verify screen                     */}
                                    {details?.verifyScreen && <Grid item container xs={12} >
                                        <Grid item xs={12}>&nbsp;</Grid>
                                        <Grid >
                                            <FxLink onClick={handleBack} id='back-icon' className="fx-signup-back-icon">
                                                <BackIcon />
                                            </FxLink>
                                        </Grid>
                                        <Grid item xs={10} sm={10} >
                                            <Typography id="signup-title" align='center' variant="h3">{props.details?.phoneNumber ? 'Verify your email' : 'Verify mobile number'}</Typography>
                                        </Grid>
                                    </Grid>}
                                </Grid>
                                {!details?.verifyScreen && <form onSubmit={handleSubmit(onSubmit)}>
                                    <div className="login-page-content-card">
                                        <>
                                            <Grid item container columnSpacing={2}>
                                                {!props.details.type && <Grid item xs={12}>
                                                    <FxButtonGroup id="signup-button-group" options={typeData} onClick={handleTypeChange} />
                                                </Grid>}
                                                {isSmallScreen &&
                                                    <div className='header-signup-brand-icon'>
                                                    <Grid>
                                                      <BrandIcon/>
                                                    </Grid>
                                                  </div>
                                                }
                                                <Grid item xs={12}>
                                                    <Typography className={`signup-page-header`}
                                                    >Create your Passport Account</Typography>
                                                </Grid>
                                                <Grid item xs={12} >&nbsp;</Grid>
                                                {/*             Individual Container                                 */}
                                                { individualCustomer && <><Grid item xs={12} className ="fx-hidden">
                                                    <FxTextEdit register={{ ...register("inviteCode") }} type="text" className={errors.inviteCode ? "border-error-input fx-input-edit login-email" : props.details?.inviteCode ? "fx-input-edit login-email fx-input-disabled" : "fx-input-edit login-email"} control={control} rules={{ required: true }} id="inviteCode_ind" label="Invite Code *" name="inviteCode" defaultValue={props.details?.inviteCode ? props.details.inviteCode : ''} isEditable={props.details?.inviteCode ? false : true} />
                                                    <div className={'error-message'}>
                                                        {errors.inviteCode && renderError(errors.inviteCode)}
                                                    </div>
                                                </Grid>
                                                    <Grid item xs={isMobileScreen?12:6}>
                                                        <FxTextEdit register={{ ...register("firstName") }} type="text" className={errors.firstName ? "border-error-input fx-input-edit login-email" : "fx-input-edit login-email"} control={control} defaultValue={props.details?.firstName ? props.details.firstName : ''} rules={{
                                                            required: true, maxLength: 40, pattern: namePatternRule, validate: (value) =>
                                                                value.trim() === value|| AUTH_STRINGS.ERRORS.NO_EXTRA_SPACES
                                                        }} id="firstName_ind" label="First Name" name="firstName"/>
                                                        <div className={'error-message'}>
                                                            {errors.firstName && renderError(errors.firstName)}
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={isMobileScreen?12:6}>
                                                        <FxTextEdit register={{ ...register("lastName") }} type="text" className={errors.lastName ? "border-error-input fx-input-edit login-email" : "fx-input-edit login-email"} control={control} defaultValue={props.details?.lastName ? props.details.lastName : ''} rules={{
                                                            required: true, maxLength: 40, pattern: namePatternRule, validate: (value) =>
                                                                value.trim() === value|| AUTH_STRINGS.ERRORS.NO_EXTRA_SPACES
                                                        }} id="lastName_ind" label="Last Name" name="lastName"/>
                                                        <div className={'error-message'}>
                                                            {errors.lastName && renderError(errors.lastName)}
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <FxTextEdit register={{ ...register("email") }} type="email" className={clsx( 'fx-input-edit login-email', { 'border-error-input': errors.emailB }, { 'fx-input-disabled': !isEditableEmail },'fs-mask' )} control={control} rules={setEmailValidationOptions} id="signup-email_ind" label="Email" name="email" defaultValue={props.details?.email ? props.details.email : ''} isEditable={isEditableEmail} />
                                                        <div className={'error-message'}>
                                                            {errors.email && renderEmailError(errors.email)}
                                                        </div>
                                                    </Grid>
                                                    {invitationType === "SMS" &&<Grid item xs={12}>
                                                    <FxPhoneEdit register={register} control={control} rules={setPhoneValidationOptions(true)} className={errors.mobilePhone ? "border-error-input fx-input-edit login-email" :props?.details?.phoneNumber ?"fx-input-edit login-email " : "fx-input-edit login-email"} id="signup-mobilePhone" name="phoneNumber" label="Phone Number" defaultCountryCode={getKey('countryCode')} defaultValue={props?.details?.phoneNumber ?? ''} isEditable={props?.details?.phoneNumber ? false :true} setValue={setValue} setError={setError} clearErrors={clearErrors} />
                                                    <div className={'error-message'}>
                                                        {errors.mobilePhone && renderMobileError(errors.mobilePhone)}
                                                    </div>
                                                    </Grid>}
                                                    <Grid item xs={12}>
                                                        <FxTextEdit passwordIcon={true} register={{ ...register("newPassword") }} control={control} rules={{
                                                            required: "Please enter a new password",
                                                            pattern: passwordPatternRule,
                                                            maxLength: { value: PASSWORD_MAX_LENGTH, message: AUTH_STRINGS.ERRORS.PASSWORD_MAX_LENGTH },
                                                        }} className={errors.newPassword ? "border-error-input fx-input-edit login-email fs-mask" : "fx-input-edit login-email fs-mask"} type="password" id="password-change-newPassoword" name="newPassword" label="Password" />
                                                        <div className={`error-message`} >
                                                            {errors?.newPassword && renderPasswordError(errors?.newPassword)}
                                                        </div>
                                                    </Grid>
                                                    
                                                    <Grid item xs={12}>
                                                        <FxTextEdit passwordIcon={true} register={{ ...register("confirmPassword") }} control={control} rules={{
                                                            required: "Please enter confirm password",
                                                            validate: (value: any) => {
                                                                const newPassword = watch("newPassword");
                                                                if (value === newPassword) {
                                                                    return true;
                                                                } else {
                                                                    return "The passwords do not match";
                                                                }
                                                            }
                                                        }} className={errors.confirmPassword ? "border-error-input fx-input-edit login-email fs-mask" : "fx-input-edit login-email fs-mask"} type="password" id="password-change-confirmPassword" name="confirmPassword" label="Confirm Password"onChange={(e) =>
                                                            handlePasswordMatchChange(e, setValue, setError, clearErrors, getValues)
                                                          } />
                                                        <div className={'error-message'}>
                                                            {errors?.confirmPassword && renderPasswordError(errors?.confirmPassword)}
                                                        </div>
                                                    </Grid>
                                                    <Grid container item xs={12} sm={12} >&nbsp;</Grid>
                                                    <Grid container item xs={12} sm={12}  alignItems="center">
                                                        <Grid item className='login-page-info-icon' xs={1}>
                                                            <InfoTooltip />
                                                        </Grid>
                                                        <Grid item xs>
                                                            <Typography variant="h5">
                                                                Use 12+ characters with uppercase, lowercase, numbers and special characters
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid container item xs={12} sm={12} >&nbsp;</Grid>
                                                    </>}
                                                {/*             Business Container                                 */}
                                                {details?.customerType === CustomerType.BUSINESS && <>
                                                    <Grid item xs={12} className ="fx-hidden">
                                                        <FxTextEdit register={{ ...register("inviteCodeB") }} type="text" className={errors.inviteCodeB ? "border-error-input fx-input-edit login-email" : props.details?.inviteCode ? "fx-input-edit login-email fx-input-disabled" : "fx-input-edit login-email"} control={control} rules={{ required: true }} id="inviteCode_bus" label="Invite Code *" name="inviteCodeB" defaultValue={props.details?.inviteCode ? props.details.inviteCode : ''} isEditable={props.details?.inviteCode ? false : true} />
                                                        <div className={'error-message'}>
                                                            {errors.inviteCodeB && renderError(errors.inviteCodeB)}
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={isMobileScreen?12:6} >
                                                        <FxTextEdit register={{ ...register("firstNameB") }} type="text" className={errors.firstNameB ? "border-error-input fx-input-edit login-email" : "fx-input-edit login-email"} control={control} defaultValue={props.details?.firstName ? props.details.firstName : ''} rules={{
                                                            required: true, maxLength: 40, pattern: namePatternRule, validate: (value) =>
                                                                value.trim() === value|| AUTH_STRINGS.ERRORS.NO_EXTRA_SPACES
                                                        }} id="firstName_bus" label="First Name" name="firstNameB" />
                                                        <div className={'error-message'}>
                                                            {errors.firstNameB && renderError(errors.firstNameB)}
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={isMobileScreen?12:6} >
                                                        <FxTextEdit register={{ ...register("lastNameB") }} type="text" className={errors.lastNameB ? "border-error-input fx-input-edit login-email" : "fx-input-edit login-email"} control={control} defaultValue={props.details?.lastName ? props.details.lastName : ''} rules={{
                                                            required: true, maxLength: 40, pattern: namePatternRule, validate: (value) =>
                                                                value.trim() === value|| AUTH_STRINGS.ERRORS.NO_EXTRA_SPACES
                                                        }} id="lastName_bus" label="Last Name" name="lastNameB" />
                                                        <div className={'error-message'}>
                                                            {errors.lastNameB && renderError(errors.lastNameB)}
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <FxTextEdit register={{ ...register("legalName") }} type="text" className={errors.legalName ? "border-error-input fx-input-edit login-email" : "fx-input-edit login-email"} control={control} rules={{ required: true, maxLength: 40 }} defaultValue={props.details?.legalName ? props.details.legalName : ''} id="legalName_bus" label="Legal Business Name" name="legalName" />
                                                        <div className={'error-message'}>
                                                            {errors.legalName && renderError(errors.legalName)}
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <FxTextEdit register={{ ...register("emailB") }} type="email" className={clsx( 'fx-input-edit login-email', { 'border-error-input': errors.emailB }, { 'fx-input-disabled': !isEditableEmail },'fs-mask' )} control={control} rules={setEmailValidationOptions} id="signup-email_bus" label="Email" name="emailB" defaultValue={props.details?.email ? props.details.email : ''} isEditable={isEditableEmail} />
                                                        <div className={'error-message'}>
                                                            {errors.emailB && renderEmailError(errors.emailB)}
                                                        </div>
                                                    </Grid>
                                                    {invitationType === "SMS" &&<Grid item xs={12} >
                                                    <FxPhoneEdit register={register} control={control} rules={setPhoneValidationOptions(true)} className={errors.mobilePhone ? "border-error-input fx-input-edit login-email" :props?.details?.phoneNumber ?"fx-input-edit login-email " : "fx-input-edit login-email"} id="signup-mobilePhone" name="phoneNumber" label="Phone Number" defaultCountryCode={getKey('countryCode')} defaultValue={props?.details?.phoneNumber ?? ''} isEditable={props?.details?.phoneNumber ? false :true} setValue={setValue} setError={setError} clearErrors={clearErrors} />
                                                    <div className={'error-message'}>
                                                    {errors.mobilePhone && renderMobileError(errors.mobilePhone)}
                                                    </div>
                                                    </Grid>}
                                                    <Grid item xs={12}>
                                                        <FxTextEdit passwordIcon={true} register={{ ...register("newPasswordB") }} control={control} rules={{
                                                            required: "Please enter a new password",
                                                            pattern: passwordPatternRule,
                                                            maxLength: { value: PASSWORD_MAX_LENGTH, message: AUTH_STRINGS.ERRORS.PASSWORD_MAX_LENGTH },
                                                        }} className={errors.newPasswordB ? "border-error-input fx-input-edit login-email fs-mask" : "fx-input-edit login-email fs-mask"} type="password" id="password-change-newPassoword" name="newPasswordB" label="Password" />
                                                        <div className={`error-message`} >                                                            
                                                            {errors?.newPasswordB && renderPasswordError(errors?.newPasswordB)}
                                                        </div>
                                                    </Grid>
                                                    
                                                    <Grid item xs={12}>
                                                        <FxTextEdit passwordIcon={true} register={{ ...register("confirmPasswordB") }} control={control} rules={{
                                                            required: "Please enter confirm password",
                                                            validate: (value: any) => {
                                                                if (value === getValues()["newPasswordB"]) {
                                                                    return true;
                                                                } else {
                                                                    return "The passwords do not match";
                                                                }
                                                            }
                                                        }} className={errors.confirmPasswordB ? "border-error-input fx-input-edit login-email fs-mask" : "fx-input-edit login-email fs-mask"} type="password" id="password-change-confirmPasswordB" name="confirmPasswordB" label="Confirm Password" 
                                                          />
                                                        <div className={'error-message'}>
                                                            {errors?.confirmPasswordB  && renderPasswordError(errors?.confirmPasswordB)}
                                                        </div>
                                                    </Grid>
                                                    
                                                    <Grid container item xs={12} sm={12} >&nbsp;</Grid>
                                                    <Grid container  item xs={12} sm={12} >
                                                        {<Grid className='login-page-info-icon' xs={1}>
                                                            <InfoTooltip />
                                                        </Grid>}
                                                        <Grid  xs>
                                                            <Typography variant="h5">
                                                                Use 12+ characters with uppercase, lowercase, numbers and special characters
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                     <Grid container item xs={12} sm={12} >&nbsp;</Grid>
                                                    </>}
                                                    
                                                
                                                <Grid item xs={12} sm={12} spacing={1} container display="flex" justifyContent="center">
                                                    <Grid item  xs={6}   sm={6} justifyContent="center">
                                                        <ReCAPTCHA  className={isMobileScreen?"fx-recaptcha-mobile":"fx-recaptcha"}
                                                            sitekey={DEFAULT_CONFIG['SITE_KEY']}
                                                            onChange={handleCaptchaChange}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} sm={6} container justifyContent="flex-end" >
                                                        <Typography className='recaptcha-label' id="captcha-description" variant="h5">Google Captcha prevents hackers and robots from accessing your Passport account.</Typography>
                                                    </Grid>
                                                    {errors.captcha && <div className={'error-message'}>
                                                        {renderError(errors.captcha)}
                                                    </div>}
                                                </Grid>
                                            </Grid>
                                            {isSmallScreen &&<span className="login-page-content-card-terms">By continuing, you agree to Passport's <a target="_blank" id="terms-of-service" href="https://prioritycommerce.com/wp-content/uploads/2024/06/Passport-End-User-License-Agreement.pdf"> End user license Agreement </a> </span>}
                                            {isSmallScreen && <Grid item xs={12} className={"login-page-bottom"}>
                                                <span className="login-page-bottom-bold" >Already have an account? <Link href="/login" >Login</Link></span>
                                            </Grid>}
                                           {!isSmallScreen &&  <Grid>
                                                <Divider />
                                            </Grid>}
                                            {isSmallScreen && <Grid className='login-page-bottom'>
                                                <FxButton className='help-button' startIcon={<NeedHelpButton />} onClick={handleExportModalOpen}>
                                                Need Help ?
                                                </FxButton>
                                                    <Drawer
                                                        anchor="bottom"
                                                        open={isModalOpen}
                                                        onClose={handleCloseDrawer}
                                                        PaperProps={{ className: 'fx-contact-us-drawer' }}
                                                    >
                                                        <ContactUsDrawer cType={details?.customerType} onClose={handleCloseDrawer} />
                                                    </Drawer>
                                            </Grid>}
                                        </>
                                        <div className={isMobileScreen?"flex login-buttonarea fx-sticky-button-container":"flex login-buttonarea"} >
                                            <FxButton
                                                disabled={!isValid}
                                                disableRipple={false}
                                                className={"fx-button login-signin"}
                                                id="signup-signin"
                                                type="submit"
                                                isSubmitting={isSubmitting}
                                            >
                                                Sign Up
                                            </FxButton>
                                        </div>
                                        {!isSmallScreen && <span className="login-page-content-card-terms">By continuing, you agree to Passport's <a target="_blank" id="terms-of-service" href="https://prioritycommerce.com/wp-content/uploads/2024/06/Passport-End-User-License-Agreement.pdf"> End user license Agreement </a> </span>}
                                    </div>
                                </form>}
                                {/* Verification code section starts here */}
                                {details?.verifyScreen &&
                                    <form onSubmit={handleSubmit(onSubmitVerify)}>
                                        <div className="login-page-content-card fx-form-edit-profile">
                                            <Grid className="flex column login-page-content-card-content">
                                                <Grid item xs={12}>
                                                    <Typography id="verify-code-title" className="form-label fx-login-info-label">{props.details?.phoneNumber ?
                                                        'We have sent an OTP to ' + maskEmail(details?.email) + ' to verify details' : 'We have sent an OTP to registered mobile number ' + details?.phoneNumber}
                                                    </Typography>
                                                </Grid>

                                                <Grid container direction="row" >
                                                    <Grid item xs={12} className="login-page-content-card-content-padding">
                                                        {/* <FxTextEdit register={{ ...register("enterCode") }} type="number" className={errors.enterCode ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} rules={{ required: true }} id="verify-code" label="Enter Code" name="enterCode" /> */}
                                                        <FxOtpInput
                                                            register={{ ...register("enterCode") }}
                                                            className={
                                                                errors.enterCode
                                                                    ? "border-error-input fx-input-edit"
                                                                    : "fx-input-edit"
                                                            }
                                                            control={control}
                                                            rules={{ required: true }}
                                                            id="verify-code"
                                                            name="enterCode"
                                                        />
                                                        {errors.enterCode && (
                                                            <div className={"error-message"}>
                                                                {renderCodeError(errors.enterCode)}
                                                            </div>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                                <Grid container alignItems="center">
                                                    <FxLocker
                                                        initiallyLocked
                                                        ref={lockerRef}
                                                        renderLocked={(remainedTime) => (
                                                            <Grid container>
                                                                <Grid item>
                                                                    Resend OTP in &nbsp;
                                                                </Grid>
                                                                <Grid item className='login-page-content-card-content-remained'>
                                                                    {`${moment.utc(remainedTime * 1000).format('mm:ss')}s`}
                                                                </Grid>
                                                            </Grid>
                                                        )}>
                                                        <Grid>
                                                            Didn't get it?
                                                        </Grid>
                                                        <Grid item justifyContent="center">
                                                            <FxResendOtp id="resend-code" className={"login-page-content-card-content-resndbtn"} onClick={handleResendCode} buttonName="Resend OTP" />
                                                        </Grid>
                                                    </FxLocker>
                                                </Grid>
                                                <div className="flex login-buttonarea">
                                                    <FxButton
                                                        disabled={watch('enterCode')?.length === 6 ? false : true}
                                                        disableRipple={false}
                                                        className={ clsx ("fx-button", "login-signin " , watch('enterCode')?.length === 6 ? "" : "fx-button-theme-disabled")}
                                                        id="forgot-password-signin"
                                                        type="submit"
                                                        isSubmitting={isSubmitting}
                                                    >
                                                        VERIFY AND SIGN UP
                                                    </FxButton>
                                                </div>
                                            </Grid>
                                        </div>
                                    </form>
                                }

                                {/* Verification code section ends here */}
                               { !isSmallScreen && <Grid item xs={12} className={"login-page-bottom"}>
                                    <span className="login-page-bottom-bold" >Already have an account? <Link href="/login" >Login</Link></span>
                                </Grid>}
                                {/* {isMobileScreen && isSmallScreen &&(
                                    <Grid item className="privacy-footer-mobile">
                                        <ShieldSignupLeftpanel />
                                        <p>
                                        We respect your privacy and keep your data safe.
                                        </p>
                                    </Grid>
                                )} */}
                            </Grid>
                        </Grid>
                    </div>
                </Grid >
            </Grid >
          </>
        );
    });
